// Cache manager for measure outcomes to improve performance
// This prevents re-fetching the same data on every click

interface CacheEntry {
  data: any;
  timestamp: number;
  key: string;
}

class MeasureOutcomesCache {
  private cache: Map<string, CacheEntry> = new Map();
  private readonly TTL = 5 * 60 * 1000; // 5 minutes cache TTL
  
  /**
   * Generate cache key for measure data
   */
  private getCacheKey(practiceId: string, measureId: string, submissionMethod: string): string {
    return `${practiceId}_${measureId}_${submissionMethod}`;
  }
  
  /**
   * Get cached data if valid
   */
  get(practiceId: string, measureId: string, submissionMethod: string): any | null {
    const key = this.getCacheKey(practiceId, measureId, submissionMethod);
    const entry = this.cache.get(key);
    
    if (!entry) {
      return null;
    }
    
    // Check if cache is expired
    if (Date.now() - entry.timestamp > this.TTL) {
      this.cache.delete(key);
      return null;
    }
    
    console.log(`[Cache] Hit for measure ${measureId}`);
    return entry.data;
  }
  
  /**
   * Store data in cache
   */
  set(practiceId: string, measureId: string, submissionMethod: string, data: any): void {
    const key = this.getCacheKey(practiceId, measureId, submissionMethod);
    
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      key
    });
    
    console.log(`[Cache] Stored data for measure ${measureId}`);
    
    // Limit cache size to prevent memory issues
    if (this.cache.size > 50) {
      // Remove oldest entries
      const entries = Array.from(this.cache.entries());
      entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
      
      // Remove oldest 10 entries
      for (let i = 0; i < 10; i++) {
        this.cache.delete(entries[i][0]);
      }
    }
  }
  
  /**
   * Invalidate cache for a specific measure
   */
  invalidate(practiceId: string, measureId: string, submissionMethod: string): void {
    const key = this.getCacheKey(practiceId, measureId, submissionMethod);
    this.cache.delete(key);
    console.log(`[Cache] Invalidated cache for measure ${measureId}`);
  }
  
  /**
   * Clear all cache
   */
  clear(): void {
    this.cache.clear();
    console.log('[Cache] Cleared all cache');
  }
  
  /**
   * Get cache statistics
   */
  getStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }
}

// Singleton instance
export const measureOutcomesCache = new MeasureOutcomesCache();
